import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { ProductLockup } from '@traceio-org/auth-ui';
import { takeCameFrom, useCameFrom } from './came-from';
import './settings-placeholder.css';

/**
 * The organization settings' placeholder: the shape of the settings page (dark bar, section navigation, the page)
 * with grey bars where the content goes. A reload of a settings page shows it, unchanged, from the first paint until
 * the page is back: the static first paint (vite/settings-placeholder-shell.ts), the boot screen, the silent restore
 * (session-restore.tsx), the callback of that restore and the settings' own loading all render this same markup, so
 * nothing changes on screen until the content appears (hosted dev 2026-10-08: Noam, "why can't it reload in place
 * with a placeholder the same shape as the app"; before, the screen went signing in, callback, skeleton, page).
 *
 * Keep the markup equal to placeholderScript's (test/settings-placeholder.test.tsx compares them).
 */
export function SettingsPlaceholder({ brand }: { brand: boolean }) {
  // Room for "Back to <product>" when the page will most likely show it (app/came-from.ts): remembered for this tab,
  // or in the address about to be read. The static first paint decides the same way.
  const { search } = useLocation();
  const remembered = useCameFrom();
  const back = remembered !== null || takeCameFrom(search).productKey !== null;
  useEffect(() => {
    document.title = 'Organization settings | Trace';
  }, []);
  return (
    <div className="trace-auth hub-ph" data-hub-placeholder="">
      <header className="hub-ph__bar trace-auth-on-dark">
        <div className="hub-ph__brand">
          {brand ? <ProductLockup lockup="trace" surface="dark" /> : null}
          <span className="hub-ph__brand-label">Organization settings</span>
        </div>
        {back ? (
          <div className="hub-ph__back" aria-hidden="true">
            <span className="hub-ph__pill hub-ph__pill--back" />
          </div>
        ) : null}
        <div className="hub-ph__account" aria-hidden="true">
          <span className="hub-ph__pill hub-ph__pill--appearance" />
          <span className="hub-ph__pill" />
          <span className="hub-ph__pill hub-ph__pill--short" />
        </div>
      </header>
      <div className="hub-ph__body">
        <div className="hub-ph__nav" aria-hidden="true">
          <div className="hub-ph__org">
            <span className="hub-ph__org-mark" />
            <Line modifier="org" />
          </div>
          <ul className="hub-ph__sections">
            {[0, 1, 2, 3, 4].map((i) => (
              <li key={i}>
                <Line modifier="section" />
              </li>
            ))}
          </ul>
        </div>
        <main className="hub-ph__main">
          <div className="hub-ph__content">
            <h1 className="trace-auth-visually-hidden">Organization settings</h1>
            <div className="hub-ph__heading" aria-hidden="true">
              <Line modifier="eyebrow" />
              <Line modifier="title" />
              <Line modifier="lead" />
            </div>
            <div className="hub-ph__panel" aria-hidden="true" />
            <div role="status" aria-live="polite" aria-busy="true">
              <span className="trace-auth-visually-hidden">Loading your organization settings</span>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function Line({ modifier }: { modifier: string }) {
  return <span className={`hub-ph__line hub-ph__line--${modifier}`} />;
}

const SETTINGS_PATH = /^\/settings(\/|$)/;

/** True for an organization settings address (the path, with or without a query). */
export function isSettingsPath(path: string): boolean {
  return SETTINGS_PATH.test(path.split('?')[0] ?? '');
}
